// Display helpers for the BookingContext the invoicing API attaches to every
// document (see BillingoInvoiceService.BookingContext).
//
// An invoice number tells an agent nothing on its own — these turn the attached
// order into the one-line "what was actually sold" they need while looking at
// the document. Pure and framework-free so they unit-test in isolation.

import dayjs from 'dayjs';

// Addresses arrive fully qualified ("… , Budapest, Hungary"), which swamps a
// table cell. Keep whole comma segments until the budget is spent, so the
// result stays a real place name rather than a mid-word truncation.
export function shortAddress(address, max = 34) {
  if (!address) return '';
  const parts = String(address).split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return '';
  let out = parts[0];
  for (let i = 1; i < parts.length; i += 1) {
    const next = `${out}, ${parts[i]}`;
    if (next.length > max) break;
    out = next;
  }
  // A single segment can still exceed the budget on its own.
  return out.length > max ? `${out.slice(0, max - 1).trimEnd()}…` : out;
}

// Trip date, optionally with the pickup time (which is what an agent checks
// against a fulfillment date).
export function formatTripWhen(iso, { withTime = true } = {}) {
  if (!iso) return '';
  const d = dayjs(iso);
  if (!d.isValid()) return '';
  return d.format(withTime ? 'D MMM YYYY HH:mm' : 'D MMM YYYY');
}

// "BUD Airport → Hotel Gellért". Falls back to whichever end is known.
export function routeLabel(booking, max = 34) {
  const from = shortAddress(booking?.from, max);
  const to = shortAddress(booking?.to, max);
  if (from && to) return `${from} → ${to}`;
  return from || to || '';
}

// "PH-1 · Ada Lovelace" — the line that identifies the order.
export function bookingHeadline(booking) {
  return [booking?.booking_reference, booking?.customer_name].filter(Boolean).join(' · ');
}

// Who an Invoicing row bills: its partner. Every booking has one — direct
// customers book through the "Pickup Hungary" partner.
export function payerLabel(row) {
  return row?.partner_name || row?.partner_id || '';
}

// "15 Jun 2026 09:30 · BUD → Gellért · 3 pax · Business"
export function bookingSubline(booking, { max = 34 } = {}) {
  const pax = booking?.passengers ? `${booking.passengers} pax` : '';
  return [
    formatTripWhen(booking?.date),
    routeLabel(booking, max),
    pax,
    booking?.category,
  ].filter(Boolean).join(' · ');
}

// Summary of everything a document bills, for a table cell. Returns structured
// parts rather than a baked string so the caller can render `extra` as a badge,
// a count, or not at all — one place decides WHAT to show, each table decides
// how.
export function documentBookingSummary(doc) {
  const bookings = doc?.bookings || [];
  if (bookings.length === 0) {
    // Pre-context documents (and legacy uploads) only carry the references.
    const refs = doc?.order_references || [];
    if (refs.length === 0) return { title: '—', detail: '', extra: 0 };
    return { title: refs[0], detail: '', extra: refs.length - 1 };
  }
  const [first] = bookings;
  return {
    title: bookingHeadline(first) || first.booking_id,
    detail: bookingSubline(first),
    extra: bookings.length - 1,
  };
}

// Free-text haystack for the invoicing search box, so an agent can find a
// document by customer name, route or event — not just its number.
export function documentSearchText(doc) {
  return [
    doc?.number,
    doc?.created_by,
    ...(doc?.order_references || []),
    ...(doc?.bookings || []).flatMap((b) => [
      b.booking_reference, b.customer_name, b.email_address,
      b.from, b.to, b.event_name, b.category,
    ]),
  ].filter(Boolean).join(' ').toLowerCase();
}
